import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  AfterViewInit,
  inject,
  input,
  output,
  viewChild,
  signal,
  computed,
  ChangeDetectionStrategy,
  SimpleChanges,
  OnChanges,
  ChangeDetectorRef,
  Signal,
} from '@angular/core';
import {
  AbstractControl,
  FormControlStatus,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  Auth,
  AuthProvider,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  UserCredential,
} from 'firebase/auth';
import { RecaptchaComponent, RecaptchaErrorParameters } from 'ng-recaptcha';
import { ReplaySubject, takeUntil } from 'rxjs';

import { AppComponent } from 'app/app.component';
import { RECAPTCHA_V2_SITE_KEY_VALUE } from 'app/config';
import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { getFeatureFlagObservable } from 'app/core/state';
import { AuthService } from 'app/services/auth.service';
import { CognitoService } from 'app/services/cognito.service';
import { FirebaseService, SignedInUser } from 'app/services/firebase.service';
import { LocalStorageService } from 'app/services/localStorage.service';
import { Interval } from 'app/types';
import { CustomValidators } from 'app/utils/custom.validators';
import { Loggable } from 'app/utils/logging/loggable';

import { CognitoCodes, getErrorMessage } from '../../CognitoCodes';
import { SignInProvider } from '../../sign-in.types';
import { toSignal } from '@angular/core/rxjs-interop';

const CAPTCHA_INTERVAL = 120000;
@Component({
  selector: 'new-sign-up-card',
  templateUrl: './new-sign-up-card.component.html',
  styleUrls: ['./new-sign-up-card.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewSignUpCardComponent extends Loggable implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  private authService = inject(AuthService);
  private cognitoService = inject(CognitoService);
  private firebaseService = inject(FirebaseService);
  private route = inject(ActivatedRoute);
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private formBuilder = inject(UntypedFormBuilder);
  private localStorageService = inject(LocalStorageService);
  private store = inject(Store);
  private changeDetectionRef = inject(ChangeDetectorRef);

  public readonly msConnector = input(false);
  public readonly msTeams = input(false);
  public readonly deploy = input(false);
  public readonly showSignIn = input(false);
  public readonly doCustomSignIn = input(false);
  public readonly doCustomSignInWithTokens = input(false);

  // mutable inputs with shadow signals
  public readonly showRegister = input(false);
  protected readonly showRegisterSignal = signal<boolean>(false);

  public readonly showLoader = input(true);
  protected readonly showLoaderSignal = signal<boolean>(true);

  public readonly selectedIndex = input(0);
  protected readonly selectedIndexSignal = signal<number>(0);

  public readonly showMobileRegister = output<boolean>();
  public readonly confirmAccount = output<string>();
  public readonly signIn = output<string>();
  public readonly forgotPassword = output<string>();
  public readonly customSignIn = output<SignInProvider>();
  public readonly customSignInWithTokens = output<SignedInUser>();

  public RECAPTCHA_V2_SITE_KEY_VALUE = RECAPTCHA_V2_SITE_KEY_VALUE;
  private captchaRef?: RecaptchaComponent;
  private captchaInterval?: Interval;

  // Form Submitted
  private readonly registerFormSubmitted = signal<boolean>(false);

  // Sign In
  public signInForm!: UntypedFormGroup;
  public signInFormLastValuePhoneNumber?: string;

  // Register
  public signUpForm?: UntypedFormGroup;
  public signUpFormLastValuePhoneNumber?: string;
  public readonly disableRegisterWithMobile = signal<boolean>(false);
  public rgxPasswordLowerAndUpper = /^(?=.*[A-Z])(?=.*[a-z])/;
  public rgxPasswordNumbers = /(?=.*\d)/;
  public rgxPasswordSpecialCharacters = /[^a-zA-Z0-9]/;
  private registerUsername?: string;
  // NEW: Signal for signUpForm status (from Observable)
  protected readonly signUpFormStatusSignal!: Signal<FormControlStatus>; // Will hold the signal value
  protected readonly signUpFormValid = computed(() => this.signUpFormStatusSignal() === 'VALID');

  // Flag Input
  public readonly isPhoneNumber = signal<boolean>(false);
  public readonly resetLoginInput = signal<boolean>(false);

  // Errors
  public readonly signInError = signal<string>('');
  public readonly stateURL = signal<string | undefined>(undefined);

  public readonly shouldShowNodeAnimation = signal<boolean>(false);
  public readonly isBrowser = signal<boolean>(false);

  private readonly appTokenInputRef = viewChild<ElementRef>('appToken');
  public readonly breakText = signal<string>('Or sign in with');
  private destroyed$ = new ReplaySubject<boolean>(1);

  protected readonly breakTextV1 = signal<string>('Or login with');
  protected readonly oAuthError = signal<string | undefined>(undefined);
  protected readonly oauthWithRedirect = signal<boolean>(false);

  public constructor() {
    super();
    this.setDisplayName('NewSignUpCardComponent');
    this.isBrowser.set(isPlatformBrowser(AppComponent.PLATFORM_ID));
    if (this.isBrowser()) {
      // Custom Mat Icons
      this.iconRegistry.addSvgIcon(
        'apple_logo',
        this.sanitizer.bypassSecurityTrustResourceUrl('../../../../assets/images/custom-icons/apple-logo.svg'),
      );
      this.iconRegistry.addSvgIcon(
        'google_logo',
        this.sanitizer.bypassSecurityTrustResourceUrl('../../../../assets/images/custom-icons/google-logo.svg'),
      );
      this.iconRegistry.addSvgIcon(
        'microsoft_logo',
        this.sanitizer.bypassSecurityTrustResourceUrl('../../../../assets/images/custom-icons/microsoft-logo.svg'),
      );
    }

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.disable_register_with_mobile)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((disableMobile) => {
        this.disableRegisterWithMobile.set(disableMobile);
        if (this.signUpForm && !this.registerFormSubmitted()) {
          this.setEmailValidators();
        }
      });

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.oauth_with_redirect)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((oauthWithRedirect) => this.oauthWithRedirect.set(oauthWithRedirect));

    this.buildForms();

    // reCAPTCHA times out every two minutes
    // call reset every two minutes
    this.captchaInterval = setInterval(() => this.resetCaptcha(), CAPTCHA_INTERVAL);
    this.registerFormSubmitted.set(false);

    if (this.signUpForm) {
      // Connect signUpForm status to signal
      this.signUpFormStatusSignal = toSignal(this.signUpForm.statusChanges.pipe(takeUntil(this.destroyed$)), {
        initialValue: this.signUpForm.status,
      }) as Signal<FormControlStatus>;
    }
  }

  public ngOnInit() {
    this.stateURL.set(this.route.snapshot.queryParamMap.get('return') ?? '');
    if (this.doCustomSignInWithTokens()) {
      this.firebaseService.setSignedInUserEmitter(this.customSignInWithTokens);
    }
  }

  // eslint-disable-next-line @angular-eslint/no-async-lifecycle-method
  public async ngAfterViewInit(): Promise<void> {
    // checking if returning from oAuth
    const auth = getAuth();
    if (auth) {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          this.info('getRedirectResult', result);
          this.complete();
          this.showLoaderSignal.set(false);
        } else {
          this.showLoaderSignal.set(false);
          this.info('no oAuth redirect result found');
        }
      } catch (error) {
        this.showLoaderSignal.set(false);
        this.error('getRedirectResult error', { error });
      }
    } else {
      this.error('auth is undefined');
      this.showLoaderSignal.set(false);
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes['showRegister']) {
      this.showRegisterSignal.set(this.showRegister());
    }
    if (changes['showLoader']) {
      this.showLoaderSignal.set(this.showLoader());
    }
    if (changes['selectedIndex']) {
      this.selectedIndexSignal.set(this.selectedIndex());
    }
  }
  public selectedTabChange(e: { index: number }): void {
    this.resetLoginInput.set(!this.resetLoginInput());
  }

  private buildForms(): void {
    const paramEmail = this.route.snapshot.queryParamMap.get('email');
    const paramFirstName = this.route.snapshot.queryParamMap.get('firstName');
    const paramLastName = this.route.snapshot.queryParamMap.get('lastName');

    // Sign In
    this.signInForm = this.formBuilder.group({
      emailSignIn: new UntypedFormControl('', [Validators.required, Validators.email]),
      passwordSignIn: new UntypedFormControl('', [Validators.required, Validators.minLength(6)]),
    });

    // Register
    this.signUpForm = this.formBuilder.group(
      {
        email: new UntypedFormControl(paramEmail || '', [
          Validators.required,
          Validators.minLength(4),
          Validators.email,
          CustomValidators.notFilledWithWhitespace,
        ]),
        firstName: new UntypedFormControl(paramFirstName || '', [
          Validators.required,
          CustomValidators.notFilledWithWhitespace,
        ]),
        lastName: new UntypedFormControl(paramLastName || '', [
          Validators.required,
          CustomValidators.notFilledWithWhitespace,
        ]),
        password: new UntypedFormControl('', [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(this.rgxPasswordLowerAndUpper),
          Validators.pattern(this.rgxPasswordNumbers),
          Validators.pattern(this.rgxPasswordSpecialCharacters),
        ]),
        confirmPassword: new UntypedFormControl('', [Validators.required]),
        termsAndConditionsAccepted: new UntypedFormControl(false, [Validators.requiredTrue]),
      },
      { validator: this.passwordConfirming },
    );
    this.setEmailValidators();
  }

  private setEmailValidators(): void {
    this.signUpForm?.get('email')?.reset();
    if (!this.disableRegisterWithMobile()) {
      this.signUpForm?.get('email')?.clearValidators();
    } else {
      this.signUpForm
        ?.get('email')
        ?.setValidators([
          Validators.required,
          Validators.minLength(4),
          Validators.email,
          CustomValidators.notFilledWithWhitespace,
        ]);
    }
    this.changeDetectionRef.markForCheck();
  }

  public passwordConfirming(c: AbstractControl): void {
    if (c.get('password')?.value !== c.get('confirmPassword')?.value) {
      c.get('confirmPassword')?.setErrors({ mustMatch: true });
    }
  }

  public onForgotPassword(): void {
    this.forgotPassword.emit(this.stateURL() ?? '');
  }

  // ============== SIGN IN ======================

  public onSignIn(): void {
    this.cognitoService
      .signIn(this.signInForm?.get('emailSignIn')?.value, this.signInForm?.get('passwordSignIn')?.value)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res) => {
          if (this.doCustomSignInWithTokens()) {
            const expiration = res.getSignInUserSession()?.getIdToken().getExpiration();
            this.localStorageService.store('exp', expiration);
          }
          this.signIn.emit(this.stateURL() ?? '');
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: (err: any) => {
          if (err['code'] === 'UserNotConfirmedException') {
            this.authService.setTempUser(
              this.signInForm?.get('emailSignIn')?.value,
              this.signInForm?.get('passwordSignIn')?.value,
            );
            this.confirmAccount.emit(this.stateURL() ?? '');
          }
          if (err['code'] === CognitoCodes.USER_NOT_FOUND) {
            this.signInError.set('There is not an account associated with this email*');
          } else {
            this.error('onSignIn sign in failed', err);
            this.signInError.set(getErrorMessage(err.code));
          }
        },
      });
  }

  // ============== REGISTER ======================

  public readonly registerDisabled = computed((): boolean => {
    // Ensure signUpForm is initialized before checking its validity
    if (!this.signUpForm) {
      return true;
    }

    if (this.registerFormSubmitted()) {
      return true;
    }

    if (!this.signUpFormValid()) {
      return true;
    }

    if (this.disableRegisterWithMobile()) {
      return false; // Enable the button.
    } else {
      const signUpFormEmailControl = this.signUpForm?.get('email');
      const signUpFormEmailValue = this.signUpForm?.get('email')?.value;
      const isEmailPresentAndValid = this.signUpFormValid() && this.signUpForm?.get('email')?.valid;

      return !(signUpFormEmailValue && isEmailPresentAndValid && !!signUpFormEmailControl); // Disable if signInForm's email is not valid/present.
    }
  });

  /**
   * Handle registration button click
   *
   * @returns
   */
  public handleRegisterClick(captchaRef: RecaptchaComponent): void {
    this.registerFormSubmitted.set(true);
    const appToken = this.appTokenInputRef()?.nativeElement?.value as string | undefined;
    if (appToken) {
      this.info('handleRegisterClick appToken provided, skipping captcha');
      this.onRegister(null, appToken);
      return;
    }
    this.captchaRef = captchaRef;
    captchaRef.execute();
  }

  public onRegister(token: string | null, appToken?: string): void {
    // AppToken bypasses captcha, so don't error if that's supplied.
    // Check if token is empty string, and retry
    if (token === '' && !appToken) {
      this.captchaRef?.execute();
      return;
    }

    if (token === null && !appToken) {
      this.signUpForm?.setErrors({ wrongRegister: 'An error occurred. Please try again.' });
      return;
    }
    const email = this.signUpForm?.get('email')?.value;
    const firstName = this.signUpForm?.get('firstName')?.value;
    const lastName = this.signUpForm?.get('lastName')?.value;
    const password = this.signUpForm?.get('password')?.value;

    if (this.disableRegisterWithMobile()) {
      this.registerUsername = email;
    } else {
      this.registerUsername = this.signInForm?.get('emailSignIn')?.value;
    }

    if (!this.registerUsername) {
      this.error('Register username is undefined');
      return;
    }
    this.cognitoService
      .createAWSUser({
        username: this.registerUsername,
        password,
        firstName,
        lastName,
        recaptchaToken: token ?? '12345',
        appToken: RECAPTCHA_V2_SITE_KEY_VALUE,
      })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.authService.setTempUser(this.registerUsername ?? '', password);
          this.confirmAccount.emit(this.stateURL() ?? '');
          this.registerFormSubmitted.set(false);
        },

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        error: (err: any) => {
          if (err.code === 'UserNotConfirmedException') {
            this.authService.setTempUser(this.registerUsername ?? '', password);
            this.confirmAccount.emit(this.stateURL() ?? '');
          } else if (err.code === 'UserLambdaValidationException') {
            this.error('onRegister recaptcha failed', { err });
            this.signUpForm?.setErrors({ wrongRegister: 'An error occurred. Please try again.' });
          } else {
            this.error('onRegister error', { err });
            this.signUpForm?.setErrors({ wrongRegister: err.message });
          }
          this.registerFormSubmitted.set(false);
        },
      });
  }

  public onError(errorDetails?: RecaptchaErrorParameters): void {
    this.error(`reCAPTCHA error encountered:`, errorDetails);
    this.signUpForm?.get('termsAndConditionsAccepted')?.reset();
    this.signUpForm?.setErrors({
      recaptchaError: 'An error occurred during reCAPTCHA verification, please try again or refresh your page.',
    });
    this.captchaRef?.reset();
  }

  private resetCaptcha(): void {
    this.captchaRef?.reset();
  }

  // ============== 3rd Party Authenticators ======================
  public oAuthSignIn(auth: Auth, provider: AuthProvider): void {
    if (this.oauthWithRedirect()) {
      signInWithRedirect(auth, provider).catch((error) => {
        this.error('oAuth error', { error });
      });
    } else {
      signInWithPopup(auth, provider)
        .then((_result: UserCredential) => {
          if (this.doCustomSignInWithTokens()) {
            return;
          }
          this.complete();
        })
        .catch((error) => {
          this.error('oAuth error', { error });
        });
    }
  }

  public appleSignIn() {
    if (this.doCustomSignIn()) {
      this.customSignIn.emit(SignInProvider.APPLE);
      return;
    }

    const auth = getAuth();
    const provider = new OAuthProvider('apple.com');
    this.oAuthSignIn(auth, provider);

    if (this.doCustomSignInWithTokens()) {
      return;
    }
  }

  public googleSignIn() {
    if (this.doCustomSignIn()) {
      this.customSignIn.emit(SignInProvider.GOOGLE);
      return;
    }

    const auth = getAuth();
    const provider = new GoogleAuthProvider();

    this.oAuthSignIn(auth, provider);

    if (this.doCustomSignInWithTokens()) {
      return;
    }
  }

  public microsoftSignIn() {
    if (this.doCustomSignIn()) {
      this.customSignIn.emit(SignInProvider.MICROSOFT);
      return;
    }

    const auth = getAuth();
    const provider = new OAuthProvider('microsoft.com');

    this.oAuthSignIn(auth, provider);

    if (this.doCustomSignInWithTokens()) {
      return;
    }
  }

  public readonly getPassword = computed((): AbstractControl | null | undefined => {
    return this.signUpForm?.get('password');
  });

  public readonly getConfirmPassword = computed((): AbstractControl | null | undefined => {
    return this.signUpForm?.get('confirmPassword');
  });

  public getPasswordValidation = (regex: RegExp) => {
    if (regex.test(this.signUpForm?.get('password')?.value)) {
      return { icon: 'done', color: 'var(--success)' };
    }
    return { icon: 'clear', color: 'var(--danger)' };
  };

  private complete() {
    this.signIn.emit(this.stateURL() ?? '');
  }

  public ngOnDestroy(): void {
    if (this.captchaInterval) {
      clearInterval(this.captchaInterval);
    }
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public handleRegisterButton(): void {
    this.selectedIndexSignal.set(1);
    this.showRegisterSignal.set(true);
    this.showMobileRegister.emit(true);
  }
}
